import React from 'react';
import { graphql, Link } from 'gatsby';
import marked from 'marked';
import slugify from 'slugify';
import yaml from 'js-yaml';

import BaseLayout from '../layouts/base-layout';

import ActionBar from '../components/common/promos/action-bar';
import AffinitiesPromo from '../components/collections/affinities-promo';
import CollectionGallery from '../components/collections/collection-gallery';
import CollectionsGrid from '../components/collections/collections-grid';
import FormattedEssay from '../components/essays/formatted-essay';
import GridHeader from '../components/common/grid-header';
import HeadWrapper from '../components/layout/head-wrapper';
import IndexFooter from '../components/essays/index-footer';
import MetaBox from '../components/collections/meta-box';
import PrintShopEmbed from '../components/common/promos/print-shop-embed';
import ShareThis from '../components/common/share-this';
import TagCloud from '../components/tags/tag-cloud';
import TopEmbed from '../components/collections/top-embed';

import { shuffle } from '../helpers/data-helpers';

import '../styles/collections/collection-view.scss';

export const Head = ({ data }) => (
	<HeadWrapper
		title={data.collection.data.Title}
	>
		<body className="has-share-buttons" />
		<meta
			id="metaTitle"
			name="title"
			content={data.collection.data.Title}
		/>
		<meta
			id="ogTitle"
			property="og:title"
			content={data.collection.data.Title}
		/>
		<meta
			id="ogImage"
			property="og:image"
			content={data.site.siteMetadata.imageHost + data.collection.data.Featured_Image_Path}
		/>
		<meta
			id="metaDescription"
			name="description"
			content={data.collection.data.Excerpt}
		/>
		<meta
			id="ogDescription"
			property="og:description"
			content={data.collection.data.Excerpt}
		/>
		<meta
			id="ogUrl"
			property="og:url"
			content={`https://publicdomainreview.org/collection/${data.collection.data.Slug}/`}
		/>	
	</HeadWrapper>
);

class Collection extends React.Component {
  constructor(props) {
    super(props);

    try {
      this.gallery = yaml.load(this.props.data.collection.data.Gallery)
    } catch (e) {
      this.gallery = null;
      console.log(e);
    }

    this.compiledTags = this.compiledTags.bind(this);

    this.state = {
      compiledRelatedCollectionEdges: [],
    }
  }

  componentDidMount() {
    this.setState({
      compiledRelatedCollectionEdges: this.compileRelatedCollections(this.props.data.collection, this.props.data.allCollections.edges),
    })
  }

  compileRelatedCollections(collection, allCollectionEdges) {
    const collectionCount = 8;
    let tagSlugs = [];
    if (collection.data.Tags) {
      tagSlugs = collection.data.Tags.map(el => el.data.Slug);
    }

    let collectionsRelatedByTag = allCollectionEdges.filter(({ node }) => {
      if (node.data.Slug === collection.data.Slug) {
        return false;
      }

      if (node.data.Tags) {
        const related = node.data.Tags.reduce((acc, el) => {
          if (!acc) {
            if (tagSlugs.includes(el.data.Slug)) {
              acc = true;
            }
          }

          return acc;
        }, false);

        return related;
      }

      return false;
    });
    collectionsRelatedByTag = shuffle(collectionsRelatedByTag);

    let collectionsRelatedByTheme = [];
    if (collectionsRelatedByTag.length < collectionCount) {
      collectionsRelatedByTheme = allCollectionEdges.filter(({ node }) => {
        if (node.data.Slug === collection.data.Slug) {
          return false;
        }

        let related = false;

        if (collection.data.Theme && node.data.Theme) {
          node.data.Theme.forEach(theme => {
            if (collection.data.Theme.includes(theme)) {
              related = true;
            }
          });
        }

        return related;
      });
      collectionsRelatedByTheme = collectionsRelatedByTheme.filter(({ node }) => {
        let unique = true;

        collectionsRelatedByTag.forEach((edge) => {
          if (edge.node.data.Slug === node.data.Slug) {
            unique = false;
          }
        });

        return unique;
      });
      collectionsRelatedByTheme = shuffle(collectionsRelatedByTheme);
    }

    const combinedRelatedCollections = collectionsRelatedByTag.concat(collectionsRelatedByTheme).slice(0, collectionCount);

    return combinedRelatedCollections;
  }

  compiledTags(collection) {
    let filteredTags = [];

    if (collection.data.Tags) {
      filteredTags = collection.data.Tags.filter(({ data }) => data.Link_Count > 1 && !data.No_Footer_Display);
      filteredTags = filteredTags.map(tag => { return { node: tag } });
    }

    return filteredTags;
  }

  render() {
    const collection = this.props.data.collection;
    
    let indexItems = [];
    this.props.data.indexCategories.edges.forEach(({ node }) => {
      const categorySlug = node.data.Slug;
      const categoryLabel = node.data.Label;
      
      node.data.Index_Listings.forEach(({ data }) => {
        const listingSlug = data.Slug;
        const listingLabel = data.Label;
        
        data.Index_Links.forEach(({ data }) => {
          const articleType = data.Composite_Slug.split('/')[1];
          const compositeSlug = data.Composite_Slug.split('/')[2];
          const slug = compositeSlug.split('#')[0];
          const paragraphRef = compositeSlug.split('#')[1];
          
          if (articleType === 'collection' && slug === collection.data.Slug) {
            indexItems.push({
              categoryLabel,
              categorySlug,
              listingLabel,
              listingSlug,
              paragraphRef
            });
          }
        });
      });
    });
    
    const date = new Date(collection.data.Published_Date);
    const utcDate = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
    
    const day = new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(utcDate);
    const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(utcDate);
    const year = new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(utcDate);
    
    const dateline = `${month} ${day}, ${year}`;

    return (
      <BaseLayout path={this.props.location.pathname}>
		<ShareThis
			variant="sticky"
			toggle={true}
			includePocket={true}
			url={`https://publicdomainreview.org/collection/${collection.data.Slug}`}
			image={this.props.data.site.siteMetadata.imageHost + collection.data.Featured_Image_Path}
		/>
		
        <div className="page-width">
          <div className="collection-view" role="main">
            <div className="collection-header">
              <div className="headline">
                {['Images', 'Books', 'Film', 'Audio'].includes(collection.data.Medium) ? (
                  <span className="label">
                    <Link to="/collections">Collections</Link> / <Link to={`/collections/${collection.data.Medium.toLowerCase()}`}>{collection.data.Medium}</Link>
                  </span>
                ) : (
                  <span className="label">
                    <Link to="/collections">Collections</Link>
                  </span>
                )}
              </div>
              <h1
                dangerouslySetInnerHTML={{ __html: marked(collection.data.Title).replace('<p>', '').replace('</p>', '') }}
              />
            </div>
            {(collection.data.Featured_Embed) ? (
              <div className="collection-embed">
                <TopEmbed data={collection.data.Featured_Embed} />
              </div>
            ) : null}
            <div className="collection-preamble">
              <div className="essay__content">
                <FormattedEssay
                  essayText={collection.data.Preamble}
                  indexItems={indexItems}
                  suppressInterstitials={true}
                />
              </div>
            </div>
            <div className="collection-byline">
              {collection.data.Primary_Author_Name && (
                <div className="attribution">
                  Text by <Link to={ `/contributor/${collection.data.Primary_Author_Reference}`}>{collection.data.Primary_Author_Name}</Link>
                  {collection.data.Secondary_Author_Name && (
                    <>
                      {` `}and <Link to={ `/contributor/${collection.data.Secondary_Author_Reference}`}>{collection.data.Secondary_Author_Name}</Link>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="collection-outbound">
              <div className="taxonomy-links">
                {(collection.data.Medium) ? (
                  <div>
                    <p className="small-metadata-headline inline">
                      Medium
                    </p>
                    <ul className="taxonomy">
                      <li>
                        {(collection.data.Medium === 'Animated GIF') ? (
                          <Link to={`/pd-remix/animated-gifs`}>
                            Animated GIF
                          </Link>
                        ) : (
                          <Link to={`/collections/${slugify(collection.data.Medium.toLowerCase())}`}>
                            {collection.data.Medium}
                          </Link>
                        )}
                      </li>
                    </ul>
                  </div>
                ) : null}

                {(collection.data.Theme) ? (
                  <div>
                    <p className="small-metadata-headline inline">
                      Theme
                    </p>
                    <ul className="taxonomy">
                      {collection.data.Theme.map((theme, idx) => (
                        <li key={`theme-${idx}`}>
                          <Link to={`/collections/all/theme/${slugify(theme.toLowerCase())}`}>
                            {theme}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {(collection.data.Genre) ? (
                  <div>
                    <p className="small-metadata-headline inline">
                      Genre
                    </p>
                    <ul className="taxonomy">
                      {collection.data.Genre.map((genre, idx) => (
                        <li key={`genre-${idx}`}>
                          <Link to={`/collections/all/genre/${slugify(genre.toLowerCase())}`}>
                            {genre}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {(collection.data.Style) ? (
                  <div>
                    <p className="small-metadata-headline inline">
                      Style
                    </p>
                    <ul className="taxonomy">
                      {collection.data.Style.map((style, idx) => (
                        <li key={`style-${idx}`}>
                          <Link to={`/collections/all/style/${slugify(style.toLowerCase())}`}>
                            {style}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {(collection.data.Type) ? (
                  <div>
                    <p className="small-metadata-headline inline">
                      Type
                    </p>
                    <ul className="taxonomy">
                      <li>
                        <Link to={`/collections/all/type/${slugify(collection.data.Type.toLowerCase())}`}>
                          {collection.data.Type}
                        </Link>
                      </li>
                    </ul>
                  </div>
                ) : null}

                {(collection.data.Epoch) ? (
                  <div>
                    <p className="small-metadata-headline inline">
                      Epoch
                    </p>
                    <ul className="taxonomy">
                      {collection.data.Epoch.map((epoch, idx) => (
                        <li key={`epoch-${idx}`}>
                          <Link to={`/collections/all/epoch/${slugify(epoch.toLowerCase())}`}>
                            {epoch}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              {(this.compiledTags(collection).length > 0) ? (
                <>
                  <p className="small-metadata-headline">
                    Tags
                  </p>
                  <div className="tags">
                    <TagCloud tagEdges={this.compiledTags(collection)} />
                  </div>
                </>
              ) : null}
              
              {indexItems && (indexItems.length > 0) && (
                <div className="essay__index essay__footer__section">
                  <h3 className="essay__footer__subhead">Indexed under…</h3>
                  <IndexFooter
                    indexItems={indexItems}
                  />
                </div>
              )}
            </div>

            <div className="collection-share">
				<div className="share-buttons">
					<ShareThis
						variant="inline"
						toggle={true}
						includePocket={true}
						url={`https://publicdomainreview.org/collection/${collection.data.Slug}`}
						image={this.props.data.site.siteMetadata.imageHost + collection.data.Featured_Image_Path}
					/>
				</div>
            </div>

            <div className="collection-meta">
              <MetaBox
                collection={collection}
                sources={this.props.data.sources}
              />
            </div>

            {(this.gallery && (
              <div className="collection-gallery">
                <CollectionGallery
                  galleryData={this.gallery}
                />
              </div>
            ))}

            {(collection.data.Postscript) ? (
              <div className="collection-postscript">
                <div className="essay__content">
                  <FormattedEssay
                    essayText={ collection.data.Postscript }
                    suppressAnchors={ true }
                  />
                </div>
              </div>
            ) : null}

            {(this.props.data.collection.data.Affinities_Promo) && (
              <div className="collection-promo">
                <AffinitiesPromo />
              </div>
            )}

            {(this.props.data.collection.data.Print_Shop) ? (
              <div className="collection-print-shop">
                <PrintShopEmbed dataYaml={this.props.data.collection.data.Print_Shop} />
              </div>
            ) : null}
			
			<div className="collection-dateline">
              <div className="dateline">
                <p className="small-metadata-headline">Published</p>
                <p className="date">
                  {dateline}
                </p>
              </div>
			</div>

            {(this.state.compiledRelatedCollectionEdges.length > 0) ? (
              <div className="collection-related">
                <GridHeader title="Related Collections" />
                <CollectionsGrid collections={ this.state.compiledRelatedCollectionEdges } />
              </div>
            ) : null}
          </div>
        </div>

        <div className="page-width">
          <ActionBar />
        </div>
      </BaseLayout>
    );
  }
}

export default Collection;

export const query = graphql`
query($slug:String) {
  site {
    siteMetadata {
      imageHost
    }
  }
  collection: airtable(table: {eq: "Collections"}, data: {Slug: {eq: $slug}}) {
    data {
      Title
      Excerpt
      Slug
      Featured_Image_Path
      Featured_Embed
      Preamble
      Gallery
      Postscript
      Print_Shop
      Affinities_Promo
      Medium
      Style
      Genre
      Theme
      Type
      Epoch
      Download
      Hat_Tip
      Source_Link
      Primary_Author_Name
      Primary_Author_Reference
      Secondary_Author_Name
      Secondary_Author_Reference
      Published_Date
      Sources {
        data {
          Title
          Slug
          Rights_Details_Group
          Rights_License_URL
          Logo_URL
          Categories
          Rights
          Rights_Summary
          Rights_Page
          Rights_Prose
          Umbrella_Title
          URL
          Umbrella_Source {
            data {
              Title
              Slug
              Logo_URL
              Child_Sources {
                data {
                  Sources {
                    data {
                      Slug
                    }
                  }
                }
              }
            }
          }
        }
      }
      Rights_Profiles {
        data {
          Label
          Slug
          Group
        }
      }
      Tags {
        data {
          Label
          Slug
          Link_Count
          No_Footer_Display
        }
      }
    }
  }
  allCollections: allAirtable(filter:{table: {eq: "Collections"}, data: {Published: {eq: true}}}) {
    edges {
      node {
        data {
          Title
          Slug
          Medium
          Theme
          Featured_Image_Path
          Tags {
            data {
              Slug
            }
          }
        }
      }
    }
  }
  sources:allAirtable(filter:{table:{eq: "Sources"}}) {
    edges {
      node {
        data {
          Title
          Slug
          Logo_URL
          Umbrella_Title
          Umbrella_Slug
          Umbrella_Logo_URL
          Umbrella_Source {
            data {
              Child_Sources {
                data {
                  Collections {
                    data {
                      Slug
                    }
                  }
                }
              }
            }
          }
          Collections {
            data {
              Slug
            }
            id
          }
          Collections {
            data {
              Slug
            }
            id
          }
        }
      }
    }
  }
  indexCategories:allAirtable(
    filter:{table: {eq:"Index Categories"}},
    sort:{data: {Sorting_Label: ASC}}
  ) {
    edges {
      node {
        data {
          Slug
          Label
          Sorting_Label
          Index_Listings {
            data {
              Slug
              Label
              Index_Links {
                data {
                  Display_Label
                  Composite_Slug
                  Composite_Featured_Image
                }
              }
            }
          }
        }
      }
    }
  }
}
`;
